import { Dialog, DialogContent, DialogTitle } from "@nimit9/signet-ui/components/dialog"
import { Input } from "@nimit9/signet-ui/components/input"
import { Kbd } from "@nimit9/signet-ui/components/kbd"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useMemo, useState } from "react"
import toast from "react-hot-toast"
import { useHotkeys } from "react-hotkeys-hook"
import { Amount } from "@/components/common/amount"
import { flattenCategories, useCategories } from "@/hooks/use-categories"
import { type CreateTransactionBody, useCreateTransaction } from "@/hooks/use-create-transaction"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { useDecryptedPersons } from "@/hooks/use-persons"
import { api } from "@/lib/api"
import { dayMonth } from "@/lib/dates"
import { pickableCategories } from "@/lib/pickable-categories"
import { parseQuickAdd, type QuickAddParse } from "@/lib/quick-add-grammar"

const QUICK_ADD_PLACEHOLDER = "450 swiggy @rohan #groceries yesterday"

/** The exact POST /transactions body for a parsed line (person is linked after create). */
function quickAddBody(p: QuickAddParse): CreateTransactionBody {
  return {
    date: p.date,
    amount: (p.amount ?? 0).toFixed(2),
    type: p.type,
    description: p.description,
    owner: "user_1",
    categoryId: p.category?.id,
    isTransfer: false,
    isIgnored: false,
  }
}

function PreviewItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-label uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className="truncate text-body text-text-primary">{children}</dd>
    </div>
  )
}

/**
 * Desktop quick add (decision e-6): one line, a deterministic grammar
 * (lib/quick-add-grammar.ts — no AI), a live parsed preview, and the same
 * create call as the Add transaction dialog (useCreateTransaction). ↵ adds
 * and closes, ⇧↵ adds and keeps the composer open for the next one.
 *
 * Persons can't be set on create (POST /transactions has no personId), so a
 * matched @person is linked with the existing PATCH /transactions/{id} right
 * after — skipped when the add was queued offline.
 */
export function QuickAddComposer({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState("")
  const [showErrors, setShowErrors] = useState(false)
  const fmt = useCurrencyFormat()
  const { data: catData } = useCategories()
  const { data: personRows } = useDecryptedPersons()
  const create = useCreateTransaction()

  const categories = useMemo(
    () =>
      pickableCategories(
        (catData ? flattenCategories(catData.data) : []).filter((c) => !c.children?.length),
      ),
    [catData],
  )
  const persons = useMemo(
    () => (personRows ?? []).filter((p) => p.name).map((p) => ({ id: p.id, name: p.name })),
    [personRows],
  )
  const parsed = useMemo(
    () => parseQuickAdd(text, { persons, categories }),
    [text, persons, categories],
  )

  async function submit(keepOpen: boolean) {
    if (parsed.errors.length > 0) {
      setShowErrors(true)
      return
    }
    const res = await create.mutateAsync(quickAddBody(parsed))
    if (!("offline" in res) && parsed.person && res.id) {
      await api.patch(`/transactions/${res.id}`, { personId: parsed.person.id })
    }
    toast(
      "offline" in res
        ? "Saved offline — will sync when you're back online."
        : `Added ${fmt(parsed.amount ?? 0)}`,
    )
    setText("")
    setShowErrors(false)
    if (!keepOpen) onClose()
  }

  const typed = text.trim().length > 0
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="quick-add-composer"
        className="top-20 right-6 left-auto translate-x-0 translate-y-0 gap-3 sm:max-w-[420px]"
      >
        <DialogTitle className="text-callout font-semibold">Quick add</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void submit(false)
          }}
        >
          <Input
            autoFocus
            aria-label="Quick add"
            placeholder={QUICK_ADD_PLACEHOLDER}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setShowErrors(false)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.shiftKey) {
                e.preventDefault()
                void submit(true)
              }
            }}
            aria-invalid={showErrors && parsed.errors.length > 0 ? true : undefined}
            disabled={create.isPending}
          />
        </form>

        <dl data-testid="quick-add-preview" className="grid grid-cols-2 gap-x-4 gap-y-2">
          <PreviewItem label="Amount">
            {parsed.amount !== null ? (
              <Amount
                value={parsed.type === "credit" ? parsed.amount : -parsed.amount}
                showSign
                precise={!Number.isInteger(parsed.amount)}
              />
            ) : (
              "—"
            )}
          </PreviewItem>
          <PreviewItem label="Merchant">{parsed.description || "—"}</PreviewItem>
          <PreviewItem label="Person">{parsed.person?.name ?? "—"}</PreviewItem>
          <PreviewItem label="Category">{parsed.category?.name ?? "—"}</PreviewItem>
          <PreviewItem label="Date">
            {parsed.dateToken ? dayMonth(parsed.date) : `Today, ${dayMonth(parsed.date)}`}
          </PreviewItem>
        </dl>

        {showErrors && parsed.errors.length > 0 ? (
          <p role="alert" className="text-caption text-destructive">
            {parsed.errors.join(" ")}
          </p>
        ) : (
          <p className="text-caption text-text-muted">
            {typed && parsed.errors.length > 0 ? `${parsed.errors[0]} · ` : ""}
            <Kbd>↵</Kbd> adds · <Kbd>⇧</Kbd>
            <Kbd>↵</Kbd> adds another · <Kbd>Esc</Kbd> closes
          </p>
        )}
        {create.isError && (
          <p role="alert" className="text-caption text-destructive">
            Couldn&apos;t add it. Try again.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Desktop-only `Q` shortcut for the composer (phone keeps the quick-add sheet). */
export function QuickAddLauncher() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const [open, setOpen] = useState(false)
  useHotkeys("q", () => setOpen(true), { enabled: isDesktop, preventDefault: true })
  if (!open || !isDesktop) return null
  return <QuickAddComposer onClose={() => setOpen(false)} />
}
